<script setup lang="ts">
import { computed } from 'vue';
import { TagValues } from '@/libs/tags-helper';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

export interface TagElement {
  tagType: string;
  tagData?: number | string;
}

const props = defineProps({
  closable: {
    type: Boolean,
    default: false,
  },
  /** dynamic i18n values */
  tagData: {
    type: [String, Number],
    default: undefined,
  },
  /** also used as locale key */
  tagType: {
    type: String,
    required: true,
  },
  /** Replaces the `tags.<tagType>` text while keeping the type's icon and style */
  label: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['close']);

const dataNumber = computed<number>(() => {
  if (typeof props.tagData === 'number') return props.tagData;
  return 1;
});

const tagIcon = computed<IconName | undefined>(() => {
  const TYPE_ICONS: { [key: string]: IconName } = {
    [TagValues.CANCELLED]: 'info',
    [TagValues.BICYLE]: 'bike',
    [TagValues.DOUBLE_DECKER]: 'layers-2',
    [TagValues.PLATFORM]: 'map-pin',
    [TagValues.PRM]: 'accessibility',
    [TagValues.NOT_PRM]: 'accessibility',
    [TagValues.TRAFFIC]: 'users',
    [TagValues.TRAFFIC_INFO_COUNT]: 'info',
    [TagValues.UPON_REQUEST]: 'hand',
    [TagValues.DELAYED]: 'info',
    [TagValues.EARLY]: 'info',
    [TagValues.UNSERVED_STOPS_COUNT]: 'info',
    [TagValues.DISPLACED_STOPS_COUNT]: 'info',
    [TagValues.TEMPORARY_STOPS_COUNT]: 'info',
    [TagValues.LOW_CROWDING]: 'users',
    [TagValues.MODERATE_CROWDING]: 'users',
    [TagValues.HIGH_CROWDING]: 'users',
    [TagValues.VERY_HIGH_CROWDING]: 'users',
  };
  return TYPE_ICONS[props.tagType];
});

// Tags reporting a disruption use the filled (warning) style from the design system
const tagClass = computed<string | null>(() => {
  const WARNING_TYPES: string[] = [
    TagValues.CANCELLED,
    TagValues.DELAYED,
    TagValues.UNSERVED_STOPS_COUNT,
    TagValues.DISPLACED_STOPS_COUNT,
    TagValues.TEMPORARY_STOPS_COUNT,
    TagValues.TRAFFIC_INFO_COUNT,
  ];
  return WARNING_TYPES.includes(props.tagType) ? 'tag--filled' : null;
});
</script>

<template>
  <div class="tag" :class="tagClass">
    <BaseIcon v-if="tagIcon" :name="tagIcon" class="tag__icon" />
    <span class="tag__text">
      {{ props.label ?? $tc('tags.' + props.tagType, dataNumber, { tagData: props.tagData }) }}
    </span>
    <BaseIcon v-if="props.closable" name="x" class="tag__icon tag__close" @click.stop="emit('close')" />
  </div>
</template>

<style lang="scss">
.tag {
  align-items: center;
  background-color: $color-white;
  border: 1px solid $color-border-light;
  border-radius: $radius-tag;
  color: $color-text-neutral;
  display: inline-flex;
  gap: 6px;
  padding: $spacing-1 6px;

  &:active {
    background-color: $color-neutral-lower;
  }

  &--filled {
    background-color: $color-warning-light;
    border: none;
    color: $color-warning-dark;
    padding: 2px 6px;

    &:active {
      background-color: $color-warning-light;
    }
  }

  &__icon {
    font-size: 16px;
  }

  &__close {
    cursor: pointer;
  }

  &__text {
    font: 500 14px / 20px $font-poppins;
    white-space: nowrap;
  }
}
</style>
